import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, Check, CheckCheck } from "lucide-react";

const kindDot = {
  info:    "bg-primary",
  success: "bg-emerald-600",
  warning: "bg-amber-600",
  error:   "bg-destructive",
};

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = () => {
    api.get("/notifications").then((r) => {
      setItems(r.data.notifications);
      setUnread(r.data.unread_count);
    }).catch(() => {});
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markAll = async () => {
    await api.post("/notifications/mark-read", {});
    load();
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        data-testid="notification-bell"
        className="relative w-9 h-9 rounded-sm border border-border grid place-items-center hover:bg-secondary transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full bg-accent text-white text-[10px] font-bold grid place-items-center font-mono-plex"
            data-testid="notification-count"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-11 w-96 border border-border bg-card shadow-xl rounded-sm z-50" data-testid="notification-dropdown">
          <div className="p-3 border-b border-border flex items-center justify-between">
            <div className="overline text-muted-foreground">Notifications</div>
            {unread > 0 && (
              <button
                onClick={markAll}
                data-testid="mark-all-read"
                className="text-xs inline-flex items-center gap-1 text-primary hover:underline font-mono-plex uppercase tracking-widest"
              >
                <CheckCheck className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <div className="p-10 text-center text-xs font-mono-plex uppercase tracking-widest text-muted-foreground">
              No notifications yet
            </div>
          ) : (
            <ul className="max-h-96 overflow-auto">
              {items.map((n) => (
                <li
                  key={n.id}
                  className={`p-3 border-b border-border last:border-b-0 flex gap-3 ${!n.read ? "bg-secondary/40" : ""}`}
                  data-testid={`notification-${n.id}`}
                >
                  <span className={`w-2 h-2 rounded-full shrink-0 mt-1.5 ${kindDot[n.kind] || kindDot.info}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">{n.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{n.body}</div>
                    <div className="text-[10px] font-mono-plex text-muted-foreground uppercase tracking-widest mt-1">
                      {new Date(n.created_at).toLocaleString()}
                    </div>
                  </div>
                  {!n.read && <Check className="w-3 h-3 text-primary shrink-0 mt-1" />}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
